import React, { useMemo, useState } from "react";
import { Flame, MessageSquare } from "lucide-react";

const COLUMNS = [
  { key: "New", accent: "border-blue-300 bg-blue-50" },
  { key: "Contacted", accent: "border-purple-300 bg-purple-50" },
  { key: "In Progress", accent: "border-amber-300 bg-amber-50" },
  { key: "Closed", accent: "border-slate-300 bg-slate-50" },
];

export default function KanbanBoard({ leads, onStatusChange, onOpen }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const grouped = useMemo(() => {
    const map = { New: [], Contacted: [], "In Progress": [], Closed: [] };
    leads.forEach((l) => {
      if (map[l.status]) map[l.status].push(l);
      else map.New.push(l);
    });
    return map;
  }, [leads]);

  const handleDrop = (col) => {
    if (dragId) {
      const lead = leads.find((l) => l.id === dragId);
      if (lead && lead.status !== col) onStatusChange(dragId, col);
    }
    setDragId(null);
    setOverCol(null);
  };

  return (
    <div data-testid="kanban-board" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {COLUMNS.map((col) => (
        <div
          key={col.key}
          data-testid={`kanban-col-${col.key.replace(/\s/g, '-').toLowerCase()}`}
          onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
          onDragLeave={() => setOverCol(null)}
          onDrop={() => handleDrop(col.key)}
          className={`rounded-2xl border-2 transition ${overCol === col.key ? "border-[#0B1E3F] " + col.accent : "border-transparent bg-slate-100/60"} p-3 min-h-[320px]`}
        >
          <div className="flex items-center justify-between px-2 pb-3">
            <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold">{col.key}</div>
            <span className="text-[11px] text-slate-500 bg-white rounded-full px-2 py-0.5">{grouped[col.key].length}</span>
          </div>
          <div className="space-y-2">
            {grouped[col.key].map((l) => (
              <div
                key={l.id}
                draggable
                onDragStart={() => setDragId(l.id)}
                onDragEnd={() => { setDragId(null); setOverCol(null); }}
                onClick={() => onOpen(l)}
                data-testid={`kanban-card-${l.id}`}
                className={`bg-white rounded-xl border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition ${dragId === l.id ? "opacity-40" : ""}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="text-sm font-semibold text-slate-800 leading-tight">{l.name}</div>
                  {l.timeline === "Under 3 months" && (
                    <span title="Hot" className="inline-flex items-center gap-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase">
                      <Flame className="w-2.5 h-2.5" /> Hot
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{l.email}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">{l.target_country} · {l.visa_type}</span>
                  {l.advisor && (
                    <span className="text-[10px] text-[#C9A961] font-semibold">{l.advisor.split(" ")[0]}</span>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
                  <span>{new Date(l.created_at).toLocaleDateString()}</span>
                  {(l.transcript || []).length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare className="w-2.5 h-2.5" /> {l.transcript.length}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {grouped[col.key].length === 0 && (
              <div className="text-center text-[11px] text-slate-400 py-6 border-2 border-dashed border-slate-200 rounded-xl">
                Drop leads here
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
